import {
  afterNextRender,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from "@angular/core";
import {
  LgButtonComponent,
  LgIconComponent,
  LgSpinnerComponent
} from "@legal-and-general/canopy";
import { ButtonIconPosition } from "@legal-and-general/canopy/lib/button/button.interface";
import { LinkComponent } from "../Link/Link.component";
import { AnalyticsService } from "../../../services/analytics/analytics.service";
import { LinkCheck } from "../../../services/link-check/link-check.service";
import { DynamicComponent } from "../../utils/dynamic-loader/dynamic.component";
import { CmsData } from "@dotcom-frontend/data";
import { DynamicComponentItem } from "../../utils/dynamic-loader/dynamic-component-item";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";
import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import {
  DOCUMENT,
  NgClass,
  NgTemplateOutlet,
  ViewportScroller
} from "@angular/common";
import { ContentApiService } from "../../../services/api/content-api.service";
import { Observable, Subscription } from "rxjs";
import { selectInLineContentIdList } from "../../../store/global/global.selectors";
import { Store } from "@ngrx/store";
import { GlobalState } from "../../../store/global/global.reducer";
import {
  removeInLineContentId,
  setInLineContentIdList
} from "../../../store/global/global.actions";

@Component({
  selector: "dotcom-frontend-button",
  templateUrl: "./Button.component.html",
  styleUrl: "./Button.component.scss",
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    LgButtonComponent,
    LgIconComponent,
    LinkComponent,
    LoaderComponent,
    NgClass,
    NgTemplateOutlet,
    LgSpinnerComponent
  ]
})
export class ButtonComponent implements OnInit, DynamicComponent {
  @Input() data: CmsData | undefined;
  @Input() tabIndex = 0;
  @ViewChild("inLineContainer") inLineContainer!: ElementRef;
  modalComponent: DynamicComponentItem | undefined;
  inlineComponent: DynamicComponentItem | undefined;
  inlineComponentIdList = [];
  inlineComponentIdList$: Observable<any> = this.store.select(
    selectInLineContentIdList
  );
  private inlineComponentIdListSubscription: Subscription = new Subscription();
  text = "";
  href: any;
  target = "";
  buttonStyle = "";
  icon: any | undefined;
  isLink = true;
  adobeData: any | undefined;
  iconAlignment: ButtonIconPosition = "right";
  isMedia = false;
  title = "";
  extension = "";
  fileSize = "";
  isModal = false;
  isInline = false;
  blockOpening: any | undefined;
  parentBlockId: string | undefined;
  isLoading = false;
  contentLoaded = false;
  inLineContainerParentId: string | undefined;
  contentLoadedId: string | undefined;
  activeBlock: number | undefined;
  scrollOffset = 100;

  constructor(
    private analyticsService: AnalyticsService,
    private linkCheck: LinkCheck,
    private componentLoaderService: ComponentLoaderService,
    public contentApiService: ContentApiService,
    @Inject(DOCUMENT) private document: Document,
    private viewportScroller: ViewportScroller,
    private renderer: Renderer2,
    public store: Store<{ global: GlobalState }>
  ) {
    afterNextRender(() => {
      this.loadInlineElement();
    });
  }

  async ngOnInit() {
    if (this.data) {
      this.text = this.data["text"];
      this.href = this.data["href"];
      this.target = this.data["target"];
      this.buttonStyle = this.data["buttonStyle"];
      this.icon = this.data["icon"];
      this.isLink = this.data["isLink"] || true;
      this.adobeData = this.data["adobeData"];
      this.title = this.data["title"];
      this.extension = this.data["extension"];
      this.fileSize = this.data["fileSize"];
      this.blockOpening = this.data["blockOpening"];
      this.activeBlock = this.data["blockId"];
      if (this.data["blockId"]) {
        this.parentBlockId = "block-" + this.data["blockId"];
      }
    }

    if (this.icon) {
      this.iconAlignment = this.icon["alignment"];
    }

    if (this.extension) {
      this.isMedia = true;
    }

    if (this.isGUID(this.href)) {
      if (this.blockOpening !== "inline") {
        const modalData = {
          contentLink: {
            id: this.href
          }
        };

        this.modalComponent = await this.componentLoaderService.loadComponent(
          "Modal",
          "globals",
          {
            modalData: modalData,
            buttonVariant: this.getButtonClass(this.buttonStyle),
            modalLinkText: this.text,
            showModalFooter: false,
            buttonIcon: this.icon
          }
        );

        this.isModal = true;
      } else if (this.blockOpening === "inline") {
        this.isInline = true;
      }

      this.contentLoadedId = this.href;
    }
  }

  getButtonClass(buttonStyle: string) {
    switch (buttonStyle) {
      case "secondary":
        return "secondary-dark";
      default:
        return "primary-dark";
    }
  }

  getGreenButton(buttonStyle: string) {
    return buttonStyle === "primary-green";
  }

  interactionClickEvent(
    adobeData: any,
    interactionContainer: any,
    interactionLink: any
  ) {
    if (adobeData) {
      this.analyticsService.adobeInteractionEvent(
        interactionContainer,
        interactionLink,
        adobeData.interactionName,
        adobeData.interactionRegion,
        adobeData.interactionType,
        adobeData.interactionPage,
        adobeData.interactionDetails
      );
    }
  }

  checkLinkAvailability(url: string) {
    return this.linkCheck.linkUnavailableCheck(url);
  }

  isGUID(urlString: string) {
    return this.linkCheck.isGUID(urlString);
  }

  loadInlineData(
    inLineContainerParentId: string | undefined,
    contentLoadedId: string | undefined
  ) {
    this.inlineComponentIdListSubscription =
      this.inlineComponentIdList$.subscribe((inlineComponentIdList) => {
        if (inlineComponentIdList) {
          this.inlineComponentIdList = inlineComponentIdList;
        }
      });
    let removeRest = false;
    let isLoaded = false;

    if (inLineContainerParentId) {
      const inLineBlocks = Array.from(
        this.document.getElementsByClassName(inLineContainerParentId)
      );

      for (const el of inLineBlocks) {
        if (contentLoadedId && el.classList.value.includes(contentLoadedId)) {
          isLoaded = true;
        } else {
          el.parentNode?.removeChild(el);
        }
      }
    }

    if (!isLoaded) {
      this.inlineComponentIdList.forEach((elementId) => {
        if (elementId === inLineContainerParentId) {
          removeRest = true;
        }
        if (removeRest) {
          this.document.getElementsByClassName(elementId).item(0)?.remove();
          this.store.dispatch(
            removeInLineContentId({ inLineContentId: elementId })
          );
        }
      });

      this.loadInlineElement();

      this.inlineComponent = undefined;
      this.contentLoaded = false;
      if (this.isGUID(this.href)) {
        this.isLoading = true;

        this.contentApiService
          .getDataById(this.href, "")
          .subscribe(async (response) => {
            if (
              response &&
              response["contentType"] &&
              response.contentType[1]
            ) {
              this.store.dispatch(
                setInLineContentIdList({
                  inLineContentIdList: [
                    ...this.inlineComponentIdList,
                    inLineContainerParentId
                  ]
                })
              );

              this.inlineComponent =
                await this.componentLoaderService.loadComponent(
                  response.contentType[1],
                  "blocks",
                  response
                );
              this.isLoading = false;
              this.contentLoaded = true;
              this.contentLoadedId = response.contentLink.guidValue;
              this.inLineContainer.nativeElement.classList.add(
                this.contentLoadedId
              );

              setTimeout(() => {
                this.scrollToAnswer(response["contentLink"]["id"]);
              }, 5);
            }
          });
      }
    }

    if (this.inlineComponentIdListSubscription) {
      this.inlineComponentIdListSubscription.unsubscribe();
    }
  }

  loadInlineElement() {
    if (this.parentBlockId && this.blockOpening === "inline") {
      const parentBlockElement = this.document.getElementById(
        this.parentBlockId
      );
      if (parentBlockElement) {
        const inlineContainer = this.inLineContainer.nativeElement;
        this.inLineContainerParentId = "parent-" + this.parentBlockId;
        inlineContainer.classList.add(this.inLineContainerParentId);

        switch (parentBlockElement.getAttribute("block-type")) {
          case "GridBoxBlock":
            parentBlockElement.parentElement?.parentElement?.after(
              inlineContainer
            );
            break;
          case "_inLineBlock":
            parentBlockElement.parentElement?.parentElement?.parentElement?.after(
              inlineContainer
            );
            break;
          default:
            parentBlockElement.after(inlineContainer);
            break;
        }
      }
    }
  }

  scrollToAnswer(blockId: any) {
    const currentBlock = this.document.getElementById(`block-${blockId}`);

    if (currentBlock !== null) {
      this.viewportScroller.setOffset([0, this.scrollOffset]);
      this.viewportScroller.scrollToAnchor(`block-${blockId}`);

      this.renderer.addClass(currentBlock, "active-block");

      this.renderer.addClass(
        currentBlock,
        "question-answer-block--subsequent-question"
      );
    }
  }
}
